import React from "react";
import {
  Box,
  Chip,
  Stack,
  Typography,
  Grid,
  Card,
  CardContent,
  Tooltip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import moment from "moment";
import { useRecoilValue } from "recoil";
import {
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
  PhotoCamera as PhotoCameraIcon,
  AppRegistration as AppRegistrationIcon,
} from "@mui/icons-material";

import AvatarName from "../../../../components/molecules/AvatarName";
import MightyDataGrid from "../../../../components/organisms/MightyDataGrid";
import {
  Protocols,
  findProtocol,
} from "../../../../hooks/useProtocolsSubscription";
import DataGridActions from "../../../../components/organisms/DataGridActions";
import useDataGridActions from "../../../../hooks/useDataGridActions";

import { DaysOfWeekString } from "../../../../data/GlobalData";

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ClientDataGrid = ({
  clients,
  additionalInfo,
  onClick,
  onDelete,
  onEdit,
}) => {
  const theme = useTheme();
  const getMealPlanById = additionalInfo.getMealPlanById;
  const lastSeenUsers = additionalInfo.lastSeenUsers;

  const protocols = useRecoilValue(Protocols);
  const dataGridActions = useDataGridActions();

  // Create client actions configuration
  const createClientActions = (client) => {
    const actions = [];

    // Add edit action if onEdit handler is provided
    if (onEdit) {
      actions.push(
        dataGridActions.createEditAction({
          onClick: (item) => onEdit(item),
          label: "Edit Client",
          eventData: {
            buttonName: "Edit Client",
            pageName: "Clients",
          },
        }),
      );
    }

    // Add delete action only for disabled clients
    if (client.status === "disabled" && onDelete) {
      actions.push(
        dataGridActions.createDeleteAction({
          onClick: (item) => onDelete(item),
          label: "Delete Client",
          title: "Delete this client?",
          message:
            "This action cannot be undone. The client and all their data will be permanently removed.",
          eventData: {
            buttonName: "Delete Client",
            pageName: "Clients",
          },
          cancelEventData: {
            buttonName: "Cancel Client Delete",
            pageName: "Clients",
          },
        }),
      );
    }

    return actions;
  };

  // Helper function to generate smart status badges
  const getStatusBadges = (client) => {
    const badges = [];
    const lastSeenRecord = lastSeenUsers[client.uid];
    const lastSeenDays = lastSeenRecord
      ? moment().diff(moment(lastSeenRecord.lastSeen.toDate()), "days")
      : -1;

    // New client (recently created)
    if (
      client.createdAt &&
      moment().diff(moment(client.createdAt.toDate()), "days") <= 7
    ) {
      badges.push({
        label: "New",
        color: "info",
        icon: <CheckCircleIcon />,
        tooltip: "Client was created within the last 7 days",
      });
    }

    // Not registered
    if (!client.authId) {
      badges.push({
        label: "Not Registered",
        color: "warning",
        icon: <AppRegistrationIcon />,
        tooltip: "Client hasn't completed their app registration process",
      });
    }

    // Dormant (not seen in 30+ days) — distinct from account Active/Disabled
    if (lastSeenDays > 30) {
      badges.push({
        label: "Dormant",
        color: "error",
        icon: <CancelIcon />,
        tooltip: `No app use in ${lastSeenDays} days (last seen)`,
      });
    }

    // Expiring soon (renew date within 7 days)
    if (client.renewDate) {
      const daysUntilRenew = moment(client.renewDate.toDate()).diff(
        moment(),
        "days",
      );
      if (daysUntilRenew <= 7 && daysUntilRenew >= 0) {
        badges.push({
          label: "Expiring Soon",
          color: "warning",
          icon: <ScheduleIcon />,
          tooltip: `Client's subscription expires in ${daysUntilRenew} day${daysUntilRenew === 1 ? "" : "s"}`,
        });
      }
    }

    // No starting photos
    if (!client.startingPhotoGroupId) {
      badges.push({
        label: "No Photos",
        color: "warning",
        icon: <PhotoCameraIcon />,
        tooltip: "Client hasn't uploaded their starting photos yet",
      });
    }

    // Old training plan (based on client's protocolAge setting, defaults to 90 days)
    if (client.protocolChanged) {
      const planAge = moment().diff(
        moment(client.protocolChanged.toDate()),
        "days",
      );
      const protocolAgeThreshold = client.protocolAge || 90;
      if (planAge > protocolAgeThreshold) {
        badges.push({
          label: "Plan Outdated",
          color: "warning",
          icon: <WarningIcon />,
          tooltip: `Training plan is ${planAge} days old and may need updating`,
        });
      }
    }

    return badges.slice(0, 3); // Limit to 3 badges to prevent overflow
  };

  const columns = React.useMemo(() => {
    return [
      {
        field: "name",
        headerName: "Name",
        minWidth: 200,
        flex: 1,
        hideable: false,
        headerAlign: "left",
        align: "left",
        renderCell: (params) => {
          return (
            <AvatarName
              showName={true}
              firstName={params.row.firstName}
              lastName={params.row.lastName}
              url={params.row.avatarUrl}
              size={32}
            />
          );
        },
      },
      {
        field: "protocolName",
        headerName: "Training Plan",
        flex: 1,
        minWidth: 180,
        headerAlign: "left",
        align: "left",
        renderCell: (params) => {
          const assigned = params.value;
          const active = params.row.activeTrainingPlanName;
          if (assigned === "Not assigned") {
            return (
              <Box style={{ color: theme.palette.primary.main }}>
                {assigned}
              </Box>
            );
          }
          if (active) {
            return (
              <Tooltip title={`Active in client app: ${active}`}>
                <span>
                  {assigned}
                  <Typography
                    component="span"
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block" }}
                  >
                    Active: {active}
                  </Typography>
                </span>
              </Tooltip>
            );
          }
          return assigned;
        },
      },
      {
        field: "lastSeen",
        headerAlign: "center",
        headerName: "Last Seen",
        width: 120,
        type: "number",
        align: "center",
        valueGetter: (params) => {
          const lastSeenRecord = lastSeenUsers[params.row.uid];
          if (!lastSeenRecord) {
            return -1;
          }

          const diff = moment().diff(
            moment(lastSeenRecord.lastSeen.toDate()),
            "days",
          );
          return diff;
        },
        renderCell: (params) => {
          if (params.value < 0) {
            return <Box style={{ color: theme.palette.primary.main }}>-</Box>;
          }

          const displayValue =
            params.value === 0
              ? "Today"
              : params.value === 1
                ? "1 day"
                : `${params.value} days`;

          return (
            <Box
              style={{
                color:
                  params.value > 7 ? theme.palette.primary.main : "inherit",
              }}
            >
              {displayValue}
            </Box>
          );
        },
      },
      {
        field: "status_badges",
        headerName: "Status",
        flex: 1,
        minWidth: 180,
        headerAlign: "left",
        align: "left",
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const badges = getStatusBadges(params.row);

          if (badges.length === 0) {
            return (
              <Tooltip title="Recent app use and no status alerts in this column">
                <Chip
                  label="Engaged"
                  color="success"
                  size="small"
                  variant="outlined"
                />
              </Tooltip>
            );
          }

          return (
            <Stack
              direction="row"
              spacing={0.5}
              flexWrap="wrap"
              sx={{ gap: 0.5 }}
            >
              {badges.map((badge, index) => (
                <Tooltip key={index} title={badge.tooltip}>
                  <Chip
                    label={badge.label}
                    color={badge.color}
                    size="small"
                    variant="outlined"
                    icon={badge.icon}
                  />
                </Tooltip>
              ))}
            </Stack>
          );
        },
      },
      {
        minWidth: 25,
        headerAlign: "center",
        align: "left",
        field: "action",
        headerName: "",
        sortable: false,
        hideable: false,
        filterable: false,
        renderCell: (params) => {
          const actions = createClientActions(params.row);

          // Only render if there are actions to show
          if (actions.length === 0) {
            return null;
          }

          return <DataGridActions item={params.row} actions={actions} />;
        },
      },
    ];
  }, [getMealPlanById, lastSeenUsers, theme]);

  // Detail panel component for secondary information
  const DetailPanel = ({ row }) => {
    const lastSeenRecord = lastSeenUsers[row.uid];
    const planAge = row.protocolChanged
      ? moment().diff(moment(row.protocolChanged.toDate()), "days")
      : -1;

    return (
      <Box sx={{ p: 3, bgcolor: "grey.50" }}>
        <Typography variant="h6" gutterBottom>
          Additional Information
        </Typography>

        <Grid container spacing={3}>
          {/* Column 1: Plan & Nutrition */}
          <Grid item xs={12} md={4}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Plan & Nutrition
                </Typography>
                <Stack spacing={1}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Plan Age
                    </Typography>
                    <Typography variant="body1">
                      {planAge >= 0 ? `${planAge} days` : "N/A"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Meal Plan
                    </Typography>
                    <Typography variant="body1">
                      {row.mealPlanId
                        ? getMealPlanById(row.mealPlanId)?.name || "Unknown"
                        : "None"}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Column 2: Schedule & Status */}
          <Grid item xs={12} md={4}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Schedule & Status
                </Typography>
                <Stack spacing={1}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Check-in Day
                    </Typography>
                    <Typography variant="body1">
                      {row.checkInDay
                        ? DaysOfWeekString[row.checkInDay]
                        : "Not Set"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Renew Date
                    </Typography>
                    <Typography variant="body1">
                      {row.renewDate
                        ? moment(row.renewDate.toDate()).format("MM/DD/YYYY")
                        : "Not Set"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Account Status
                    </Typography>
                    <Chip
                      label={row.status === "disabled" ? "Disabled" : "Active"}
                      color={row.status === "disabled" ? "error" : "success"}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Column 3: Registration & Technical */}
          <Grid item xs={12} md={4}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Registration & Technical
                </Typography>
                <Stack spacing={1}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Registration Status
                    </Typography>
                    <Chip
                      label={row.authId ? "Registered" : "Not Registered"}
                      color={row.authId ? "success" : "warning"}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Starting Photos
                    </Typography>
                    <Chip
                      label={row.startingPhotoGroupId ? "Yes" : "No"}
                      color={row.startingPhotoGroupId ? "success" : "warning"}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      App Version
                    </Typography>
                    <Typography variant="body1">
                      {lastSeenRecord?.appVersion || row.appVersion || "-"}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const rows = React.useMemo(() => {
    return clients.map((client) => {
      return {
        id: client.uid,
        ...client,
        protocolName: (() => {
          const primaryId = client.primaryProtocolId || null;
          const activeId = client.protocolId || null;
          const primaryRow = primaryId
            ? findProtocol({ protocolId: primaryId, protocols })
            : null;
          const activeRow = activeId
            ? findProtocol({ protocolId: activeId, protocols })
            : null;
          return (
            primaryRow?.name || activeRow?.name || "Not assigned"
          );
        })(),
        activeTrainingPlanName: (() => {
          const primaryId = client.primaryProtocolId || null;
          const activeId = client.protocolId || null;
          const primaryRow = primaryId
            ? findProtocol({ protocolId: primaryId, protocols })
            : null;
          const activeRow = activeId
            ? findProtocol({ protocolId: activeId, protocols })
            : null;
          if (
            !primaryId ||
            !activeId ||
            primaryId === activeId ||
            !primaryRow ||
            !activeRow
          ) {
            return null;
          }
          return activeRow.name || null;
        })(),
      };
    });
  }, [clients, protocols]);

  return (
    <MightyDataGrid
      entityName="clients"
      rows={rows}
      columns={columns}
      onRowClick={onClick}
      getDetailPanelContent={({ row }) => <DetailPanel row={row} />}
      getDetailPanelHeight={() => "auto"}
      viewportOffset={410} // Adjust based on your header height + search input
    />
  );
};

export default ClientDataGrid;
